import * as React from 'react';

import Form from '@components/library/form/Form';
import InputContainer from '@components/library/input_container/InputContainer';
import { InputType, InputsMap, SelectionValue, ValidationRules } from '@components/library/inputs/Input.props';
import { CustomErrorMapping } from '@components/library/form/Form.props';
import { LabelPosition, StandardSwitch, SwitchSize } from '@components/library/switch';

import './styles/main.scss';

type Map = { [id: number]: string };

interface CreateFtpIngestFormProps {
  sections: Map;
  collections: Map;
  gettyClient: boolean;
  children?: JSX.Element[];
}

const usernameName = 'username';
const passwordName = 'password';
const sectionSelectName = 'section';
const collectionSelectName = 'collection';
const allowFtpId = 'ftp-ingest-allow-ftp';
const usernameTakenErrors = ['file or folder already exists with that name', 'Username has already been taken'];
const badPasswordError = 'Password is among the most common passwords and therefore can not be used';

const onAsyncCustomValidation = async (inputsMap: InputsMap, allowFtp: boolean, setError: SetStateDispatch<boolean>):
  Promise<CustomErrorMapping[]> => {
  const { username, password, section, collection } = inputsMap;
  const collectionId = collection && ((collection.value) as SelectionValue).value;
  const sectionId = ((section.value) as SelectionValue).value;
  const body = {
    username: username.value,
    password: password.value,
    section: sectionId,
    collection: collectionId,
    ftp_permission: allowFtp
  };
  const url = `/${BFG.resource.slug}/bulk_management/ftp_ingest/create`;
  try {
    const { signal } = new AbortController();
    const options = {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${BF_Token}`, // eslint-disable-line @typescript-eslint/camelcase
        'Content-Type': 'application/json'
      },
      signal
    };
    const resp = await fetch(url, { ...options, body: JSON.stringify(body) });
    const json = resp.status === 202 || resp.status === 204 ? {} : await resp.json();
    const errorMapping: CustomErrorMapping[] = [];
    if (json.error) {
      if (usernameTakenErrors.includes(json.error)) {
        const inputName = usernameName;
        const errorString = bfTranslate("This username is already taken");
        errorMapping.push({ inputName, errorString });
      }

      if (json.error === badPasswordError) {
        const inputName = passwordName;
        const errorString = bfTranslate("Please select a less common password");
        errorMapping.push({ inputName, errorString });
      }

      setError(true);
    }

    return errorMapping;
  } catch (e) {
    throw e;
  }
};

const mapToOptions = (map: Map): SelectionValue[] => {
  const keys = Object.keys(map);
  return keys.map((key) => ({ value: key, label: map[key] }));
};

const CreateFtpIngestForm = (props: CreateFtpIngestFormProps): JSX.Element => {
  const [error, setError] = React.useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [allowFtp, setAllowFtp] = React.useState(false);
  const sectionOptions = mapToOptions(props.sections);
  const collectionOptions = mapToOptions(props.collections);
  const selectValidationRules: ValidationRules = { isRequired: false };

  return (
    <div className="ftp-ingest-form">
      {props.gettyClient && (
        <div className="ftp-ingest-toggle-container">
          <StandardSwitch
            id={allowFtpId}
            isChecked={allowFtp}
            labelPosition={LabelPosition.Right}
            onChange={(): void => setAllowFtp(!allowFtp)}
            size={SwitchSize.Large}
          >
            {`${bfTranslate("Allow insecure")} FTP`}
          </StandardSwitch>
        </div>
      )}
      <Form
        onAsyncCustomValidation={
          (inputsMap: InputsMap): Promise<CustomErrorMapping[]> => onAsyncCustomValidation(inputsMap, allowFtp, setError)
        }
        onSubmit={(): void => window.location.reload(true /* forceGET */)}
        submitButtonText={bfTranslate("Create FTP Ingest")}
      >
        <InputContainer attributes={{ type: InputType.Text, name: usernameName }} />
        <InputContainer
          attributes={{
            type: InputType.Password,
            name: passwordName,
          }}
          className="ftp-ingests-password-input-container"
          validationRules={{ isRequired: true, minLength: 8 }}
        >
          <div className="ftp-ingests-password-warning">
            <div className="bff-warning ftp-ingests-password-warning-icon" />
            <div className="ftp-ingests-password-warning-details">
              <div>
                {bfTranslate("Passwords can not be recovered or reset.")}
              </div>
              <div>
                {bfTranslate("Please make a record of your password.")}
              </div>
            </div>
          </div>
        </InputContainer>
        <InputContainer
          attributes={{ type: InputType.Select, name: sectionSelectName }}
          labelCopy={bfTranslate("Add assets to section")}
          options={sectionOptions}
          selectValue={sectionOptions[0]}
          validationRules={selectValidationRules}
        />
        {collectionOptions.length > 0 ? (
          <InputContainer
            attributes={{ type: InputType.Select, name: collectionSelectName }}
            labelCopy={bfTranslate("Add assets to Collection (Optional)")}
            options={collectionOptions}
            validationRules={selectValidationRules}
          />
        ) : <></>}
      </Form>
    </div>
  );
};

export default CreateFtpIngestForm;
